import * as React from 'react';
import { formatValue, HUNDRED_PCT, roundToHundreds } from '../../../utils/common';
import { DailyUsage, filterByEntityAndDatesUsage, Resource } from '../../../../store/ResourcesListStore';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { TimeType, TimeTypesConfig, timeTypesConfigMap } from './common';
import { calculateSummary } from './Cell';
import { ViewType } from '../ResourceUsageGrid';
import { EntityType, mapServerEntityType } from '../../../../entities/common';
import { getDefaultCostRateTable } from '../../../../store/selectors/costRateTableSelectors';
import CellValue from './CellValue';
import { usePrevious } from '../../../utils/effects';
import { arraysEqual } from 'office-ui-fabric-react';
import { CalendarsState, getEntityCalendar } from '../../../../store/CalendarsListStore';

interface StateProps {
    resourcePlanningLevel: EntityType;
    defaultCostRateTableId?: string;
    calendars : CalendarsState;
}
interface OwnProps {
    resources: Resource[];
    entityIds: string[];
    entityType: EntityType;
    startDate: Date;
    finishDate: Date;
    timeType: TimeType[];
    viewType: ViewType;
}

type Props = StateProps & OwnProps;
export interface TotalHours {
    plan: number;
    actual: number;
    remaining: number;
    capacity: number;
    availability: number;
    planPercent?: number;
    actualPercent?: number;
    remainingPercent?: number;
    capacityPercent?: number;
    availabilityPercent?: number;
    planFTE?: number;
    actualFTE?: number;
    remainingFTE?: number;
    capacityFTE?: number;
    availabilityFTE?: number;
}
export interface ResourceHours {
    plan: number;
    actual: number;
    remaining: number;
    capacity: number;
    availability: number;
    fullFteCapacityHours: number;
}

const TotalUsageCell = (props: Props) => {
    const [state, setState] = React.useState<TotalHours>({plan: 0, actual: 0, remaining: 0, capacity: 0, availability: 0});
    const prevProps = usePrevious(props);
    React.useEffect(        
        () => {
            if(!prevProps || !arraysEqual(props.entityIds, prevProps.entityIds)
                || props.calendars.byId !== prevProps.calendars.byId
                || props.calendars.archivedById !== prevProps.calendars.archivedById
                || props.resources !== prevProps.resources
                || props.startDate.getTime() !== prevProps.startDate.getTime()
                || props.finishDate.getTime() !== prevProps.finishDate.getTime()
                || props.entityType !== prevProps.entityType
                || props.resourcePlanningLevel !== prevProps.resourcePlanningLevel
            ){
                setState(buildState(props));
            }
        }, 
        [props]);

    return <TotalCellValues hours={state} timeType={props.timeType} viewType={props.viewType} timeTypesConfig={timeTypesConfigMap} />;
}

interface TotalCellValuesProps {
    hours: TotalHours;
    timeType: TimeType[];
    viewType: ViewType;
    timeTypesConfig: TimeTypesConfig;
}

export const TotalCellValues = ({ hours, timeType, viewType, timeTypesConfig }: TotalCellValuesProps) => {
    const { plan, actual, remaining, capacity, availability, planPercent, actualPercent, remainingPercent, capacityPercent,
        availabilityPercent, planFTE, actualFTE, remainingFTE, capacityFTE, availabilityFTE } = hours;

    const unit = viewType === ViewType.Percent ? '%' : viewType === ViewType.FTE ? 'FTE' : 'hours';
    const planValue = viewType === ViewType.Percent ? planPercent : viewType === ViewType.FTE ? planFTE : plan;
    const actualValue = viewType === ViewType.Percent ? actualPercent : viewType === ViewType.FTE ? actualFTE : actual;
    const remainingValue = viewType === ViewType.Percent ? remainingPercent : viewType === ViewType.FTE ? remainingFTE : remaining;
    const capacityValue = viewType === ViewType.Percent ? capacityPercent : viewType === ViewType.FTE ? capacityFTE : capacity;
    const availabilityValue = viewType === ViewType.Percent ? availabilityPercent : viewType === ViewType.FTE ? availabilityFTE : availability;

    return <div className="resource-usage-cell fill-space total" style={{ position: "relative" }}>
        <div className={`cell-content fill-space columns-${timeType.length}`}>
            {timeType.includes('plan') && <CellValue value={formatValue(planValue)} unit={unit} isEmpty={false} title={timeTypesConfig.plan.title}
                className={timeTypesConfig.plan.className} />}
            {timeType.includes('actual') && <CellValue value={formatValue(actualValue)} unit={unit} isEmpty={false} title={timeTypesConfig.actual.title}
                className={timeTypesConfig.actual.className} />}
            {timeType.includes('remaining') && <CellValue value={formatValue(remainingValue)} unit={unit} isEmpty={false} title={timeTypesConfig.remaining.title}
                className={timeTypesConfig.remaining.className} />}
            {timeType.includes('availability') && <CellValue value={formatValue(availabilityValue)} unit={unit} isEmpty={false} title={timeTypesConfig.availability.title}
                className={timeTypesConfig.availability.className} />}
            {timeType.includes('capacity') && <CellValue value={formatValue(capacityValue)} unit={unit} isEmpty={false} title={timeTypesConfig.capacity.title}
                className={timeTypesConfig.capacity.className} />}
        </div>
    </div>;
}

const buildState = (props: Props): TotalHours => toTotalHours(props.resources.map(resource => {
    const calendar = getEntityCalendar(props.calendars, resource, resource.calendar.id);
    const usages = props.entityIds.reduce<DailyUsage[]>((entityAcc, entityId) =>
        [...entityAcc, ...filterByEntityAndDatesUsage(resource.usage, entityId, props.startDate, props.finishDate)], []);
    const summary = calculateSummary(resource, props.startDate, props.finishDate, calendar, props.entityType, props.resourcePlanningLevel, props.defaultCostRateTableId);

    return {
        plan: usages.reduce((sum, u) => sum + u.plannedHours, 0),
        actual: usages.reduce((sum, u) => sum + u.actualHours, 0),
        remaining: 0,
        capacity: summary.capacityHours,
        availability: summary.availability,
        fullFteCapacityHours: summary.fullFteCapacityHours
    };
}));

export const toTotalHours = (resourceHours: ResourceHours[]): TotalHours => {
    const sum = (value: (hours: ResourceHours) => number) => resourceHours.reduce((acc, _) => acc + value(_), 0);
    const sumFte = (value: (hours: ResourceHours) => number) =>
        roundToHundreds(sum(_ => _.fullFteCapacityHours ? value(_) / _.fullFteCapacityHours : 0));

    const plan = sum(_ => _.plan);
    const actual = sum(_ => _.actual);
    const remaining = sum(_ => _.remaining);
    const capacity = sum(_ => _.capacity);
    const availability = sum(_ => _.availability);

    const planPercent = capacity ? Math.round(plan / capacity * HUNDRED_PCT) : 0;
    const actualPercent = capacity ? Math.round(actual / capacity * HUNDRED_PCT) : 0;
    const remainingPercent = capacity ? Math.round(remaining / capacity * HUNDRED_PCT) : 0;
    const capacityPercent = capacity ? HUNDRED_PCT : 0;
    const availabilityPercent = capacity ? Math.round(availability / capacity * HUNDRED_PCT) : 0;

    return {
        plan, actual, remaining, capacity, availability,
        planPercent, actualPercent, remainingPercent, capacityPercent, availabilityPercent,
        planFTE: sumFte(_ => _.plan),
        actualFTE: sumFte(_ => _.actual),
        remainingFTE: sumFte(_ => _.remaining),
        capacityFTE: sumFte(_ => _.capacity),
        availabilityFTE: sumFte(_ => _.availability)
    };
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        calendars: state.calendars,
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
    };
}

export default connect(mapStateToProps)(TotalUsageCell);